import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {sendEvent, stringifyObjectValues} from '@github-ui/hydro-analytics'

import {referenceID, referencesAreEqual} from './copilot-chat-helpers'
import {
  computeReplacedReferences,
  finalizeStreamingExecutions,
  findMatchingExecutionIndex,
} from './copilot-chat-reducer-helpers'
import type {CopilotChatAction, CopilotChatState, Timings} from './copilot-chat-reducer-types'
import type {
  CopilotAnnotations,
  CopilotChatMessage,
  CopilotChatReference,
  CopilotChatThread,
  SkillExecution,
} from './copilot-chat-types'

/**
 * Emits analytics events for an action at the dispatch boundary, reading the state as it was
 * *before* the reducer runs (`prev`). Telemetry is a side effect, so it lives here rather than
 * inside `copilotChatReducer`, keeping the reducer a pure `(state, action) => state` function that
 * is safe to run during SSR and to replay (e.g. React StrictMode double-invoke).
 *
 * Payloads that depend on values the reducer derives internally are reconstructed from
 * `(prev, action)` — using the same shared helpers the reducer uses — so the two never drift.
 *
 * Note: `prev` reflects the last committed state. When several actions are dispatched
 * synchronously within a single event, count-style payloads read the pre-batch state; this is an
 * accepted trade-off for best-effort analytics and matches the announcement boundary pattern.
 */
export function emitChatTelemetry(prev: CopilotChatState, action: CopilotChatAction): void {
  switch (action.type) {
    case 'SLASH_COMMANDS_ERROR':
      sendEvent('copilot.slash_commands_error')
      break
    case 'SLASH_COMMANDS_LOADED':
      sendEvent('copilot.slash_commands_loaded')
      break
    case 'OPEN_COPILOT_CHAT':
      sendEvent('copilot.open_copilot_chat', {source: action.source})
      break
    case 'CLOSE_COPILOT_CHAT':
      sendEvent('copilot.close_copilot_chat')
      break
    case 'HIDE_COPILOT_CHAT':
      sendEvent('copilot.hide_copilot_chat')
      break
    case 'THREAD_CREATED':
      sendEvent('copilot.thread_created', {
        ...stringifyThread(action.thread),
        mode: prev.mode,
        count: prev.threads.size + 1,
        defaultToAgent: copilotFeatureFlags.defaultToAgentInput,
        defaultToAgentControl: copilotFeatureFlags.defaultToAgentControl,
        askModeDropdown: copilotFeatureFlags.askModeDropdown,
      })
      break
    case 'THREAD_CONTINUED':
      sendEvent('copilot.thread_continued', {
        ...stringifyThread(action.thread),
        mode: prev.mode,
        count: prev.threads.size + 1,
      })
      break
    case 'THREAD_CONTINUED_FAILED':
      sendEvent('copilot.thread_continued_failed', {
        stateSelectedThreadID: prev.selectedThreadID,
        sharedIdOnFetch: prev.fetchSharedThreads?.shareId,
        mode: prev.mode,
      })
      break
    case 'CLEAR_CURRENT_REFERENCES':
      sendEvent('copilot.clear_current_references')
      break
    case 'CLEAR_ALL_REFERENCES':
      sendEvent('copilot.clear_all_references')
      break
    case 'MESSAGES_UPDATED':
      if (action.state === 'error') {
        sendEvent('copilot.messages_updated', {count: action.messages?.length, loading: action.state})
      }
      break
    case 'SELECT_THREAD':
      sendEvent('copilot.select_thread', {
        threadID: action.thread?.id,
        mode: prev.mode,
      })
      break
    case 'HANDLE_EVENT_START':
      sendEvent('copilot.handle_event_start')
      break
    case 'THREADS_LOADED':
      sendEvent('copilot.threads_loaded', {count: action.threads.length, mode: prev.mode})
      break
    case 'THREADS_LOADING_ERROR':
      sendEvent('copilot.threads_loading_error', {error: action.message})
      break
    case 'DELETE_THREAD_KEEP_SELECTION':
      sendEvent('copilot.thread_deleted', {
        ...stringifyThread(action.thread),
        count: prev.threads.size - 1,
      })
      break
    case 'DELETE_ALL_THREADS_KEEP_SELECTION': {
      // The reducer deletes threads one at a time from a working copy and logs the shrinking
      // count before each delete; replicate that bookkeeping so the per-thread counts match.
      const remaining = new Map(prev.threads)
      for (const thread of action.threads) {
        sendEvent('copilot.thread_deleted', {
          ...stringifyThread(thread),
          count: remaining.size - 1,
        })
        remaining.delete(thread.id)
      }
      break
    }
    case 'DELETE_THREAD':
      sendEvent('copilot.thread_deleted', {
        ...stringifyThread(action.thread),
        count: prev.threads.size - 1,
        mode: prev.mode,
      })
      break
    case 'DELETE_THREAD_ERROR':
      sendEvent('copilot.delete_thread_error', {...stringifyThread(action.thread), error: action.error})
      break
    case 'DELETE_ALL_THREADS_ERROR':
      for (const thread of action.threads) {
        sendEvent('copilot.delete_thread_error', {...stringifyThread(thread), error: action.error})
      }
      break
    case 'MESSAGE_ADDED':
      sendEvent('copilot.message_added', {
        ...stringifyMessage(action.message),
        count: prev.messages.length + 1,
        repoHasCustomInstructions: Boolean(action.repoHasCustomInstructions),
        usedRepoCustomInstructions: Boolean(action.usedRepoCustomInstructions),
        customInstructionsOwners: action.customInstructionsOwners || '',
      })
      break
    case 'MESSAGE_FEEDBACK':
      // Mirror the reducer: only logged when the targeted message can't be found in state.
      if (!prev.messages.some(m => m.id === action.message.id)) {
        sendEvent('copilot.message_feedback_no_message_found', {
          ...stringifyMessage(action.message),
        })
      }
      break
    case 'MESSAGES_SET_SELECTED_MESSAGE':
      sendEvent('copilot.chat_subthread_changed', {
        removedReferences: prev.currentReferences.length,
      })
      break
    case 'REFERENCES_LOADED':
      sendEvent('copilot.references_loaded', {count: action.references.length})
      break
    case 'ADD_REFERENCE':
      // The reducer only logs when the reference is actually new (dedup didn't drop it).
      if (!prev.currentReferences.some(reference => referencesAreEqual(reference, action.reference))) {
        sendEvent('copilot.add_reference', {
          ...stringifyReference(action.reference),
          source: action.source,
          count: prev.currentReferences.length + 1,
          implicit: !!action.implicit,
        })
      }
      break
    case 'REMOVE_REFERENCES':
      if (action.references.length > 0) {
        sendEvent('copilot.remove_reference', {
          count: prev.currentReferences.length - action.references.length,
        })
      }
      break
    case 'REPLACE_REFERENCE': {
      const {updatedReferences} = computeReplacedReferences(
        prev.currentReferences,
        prev.implicitReferences,
        action.referenceToDelete,
        action.referenceToInsert,
      )
      sendEvent('copilot.replace_reference', {
        removedReference: referenceID(action.referenceToDelete),
        addedReference: referenceID(action.referenceToInsert),
        count: updatedReferences.length,
      })
      break
    }
    case 'CURRENT_TOPIC_UPDATED':
      if (action.topic) {
        sendEvent('copilot.current_topic_updated', {
          type: !action.topic ? 'none' : 'repository',
          mode: prev.mode,
        })
      }
      break
    case 'MESSAGE_STREAMING_STARTED':
      sendEvent('copilot.message_streaming_started', {
        ...stringifyMessage(action.message),
        model: prev.model?.id,
        mode: prev.mode,
      })
      break
    case 'MESSAGE_STREAMING_FUNCTION_CALLED': {
      // The reducer logs only when a completed call has no matching in-flight execution.
      if (!prev.streamingMessage || action.status !== 'completed') break
      const prevExecutions = prev.streamingMessage.skillExecutions || []
      if (findMatchingExecutionIndex(prevExecutions, action) < 0) {
        sendEvent('copilot.function_call_missing_callid', {
          referenceCount: action.references.length,
          tool: action.name,
          callId: action.callId ?? 'missing',
        })
      }
      break
    }
    case 'MESSAGE_STREAMING_COMPLETED': {
      if (!prev.streamingMessage) break
      const completedMessage: CopilotChatMessage = {
        ...prev.streamingMessage,
        id: action.messageResponse.id,
        references: action.messageResponse.references,
        createdAt: action.messageResponse.createdAt,
        intent: action.messageResponse.intent,
        copilotAnnotations: action.messageResponse.copilotAnnotations,
        skillExecutions: finalizeStreamingExecutions(prev.streamingMessage.skillExecutions),
      }
      sendEvent('copilot.message_streaming_completed', {
        ...stringifyMessage(completedMessage),
        ...stringifyTiming(action.timings),
        model: action.messageResponse.model ?? prev.model?.id,
        mode: prev.mode,
        count: prev.messages.length + 1,
      })
      break
    }
    case 'MESSAGE_STREAMING_FAILED':
      sendEvent('copilot.message_streaming_failed', {
        ...(prev.streamingMessage ? stringifyMessage(prev.streamingMessage) : {}),
        ...stringifyTiming(action.timings),
        // Prefer the BE-resolved routed model (set by MESSAGE_STREAMING_ROUTED_MODEL on Auto turns).
        model: prev.streamingMessage?.model ?? prev.model?.id,
        mode: prev.mode,
      })
      break
    case 'MESSAGE_STREAMING_STOPPED':
      // The reducer only logs when a response had already begun streaming.
      if (prev.streamingMessage) {
        sendEvent('copilot.message_streaming_stopped', {
          ...stringifyMessage({...prev.streamingMessage, interrupted: true}),
          ...stringifyTiming(action.timings),
          model: prev.streamingMessage.model ?? prev.model?.id,
          mode: prev.mode,
          count: prev.messages.length + 1,
        })
      }
      break
    case 'SELECT_REFERENCE':
      if (action.reference) {
        sendEvent('copilot.select_reference', stringifyReference(action.reference))
      }
      break
    case 'SELECT_MODEL':
      // The Claude vision gate only toggles a capability, never the model name, so the un-gated
      // action model yields the same telemetry value.
      sendEvent('copilot.select_model', {model: action.model?.name, mode: prev.mode})
      break
    case 'TOGGLE_REPO_CUSTOM_INSTRUCTIONS':
      sendEvent('copilot.repo_custom_instructions_toggled', {
        isEnabling: Boolean(action.value),
      })
      break
    case 'SHARED_THREAD_UPDATED': {
      const storedThread = prev.threads.get(action.thread.id)
      if (storedThread) {
        sendEvent('copilot.shared_thread_update', stringifyThread({...storedThread, ...action.thread}))
      }
      break
    }
    case 'SHARED_THREADS_LOADED':
      sendEvent('copilot.shared_threads_loaded')
      break
    case 'SHARED_THREADS_LOADING_ERROR':
      sendEvent('copilot.shared_threads_loading_error', {error: action.message})
      break
    case 'START_EDITING_MESSAGE':
      sendEvent('dotcom_chat.activate', {target: 'USER_MESSAGE_ACTION_EDIT', mode: 'immersive'})
      break
  }
}

/**
 * Stringifies the given message while cleaning sensitive information from it.
 */
function stringifyMessage(message: CopilotChatMessage) {
  if (!message) {
    return stringifyObjectValues({})
  }
  const obj: Record<string, unknown> = {
    id: message.id,
    role: message.role,
    createdAt: message.createdAt,
    threadID: message.threadID,
    referenceCount: message.references?.length ?? 0,
  }
  if (message.intent) {
    obj.intent = message.intent
  }
  if (message.error) {
    obj.error = message.error
  }
  if (message.copilotAnnotations) {
    obj.copilotAnnotations = cleanCopilotAnnotations(message.copilotAnnotations)
  }
  if (message.skillExecutions) {
    obj.skillExecutions = cleanSkillExecutions(message.skillExecutions)
  }
  if (message.interrupted) {
    obj.interrupted = true
  }
  return stringifyObjectValues(obj)
}

/**
 * Stringifies the given thread while cleaning sensitive information from it.
 */
function stringifyThread(thread: CopilotChatThread) {
  if (!thread) {
    return stringifyObjectValues({})
  }
  return stringifyObjectValues({
    id: thread.id,
    createdAt: thread.createdAt,
    updatedAt: thread.updatedAt,
    currentReferenceCount: thread.currentReferences?.length ?? 0,
  })
}

/**
 * Stringifies the given reference while cleaning sensitive information from it.
 */
function stringifyReference(reference: CopilotChatReference) {
  if (!reference) {
    return stringifyObjectValues({})
  }
  return stringifyObjectValues({
    type: reference.type,
  })
}

/**
 * Remove sensitive information from skillExecutions in preparation for logging.
 */
function cleanSkillExecutions(skillExecutions: SkillExecution[] | undefined) {
  if (!skillExecutions) {
    return []
  }
  return skillExecutions.map(execution => ({
    slug: execution?.slug,
    status: execution?.status,
    argumentCount: execution?.arguments?.length ?? 0,
    errorMessage: execution?.errorMessage,
    references: execution?.references?.length ?? 0,
  }))
}

/**
 * Remove sensitive information from copilotAnnotations in preparation for logging.
 */
function cleanCopilotAnnotations(annotations: CopilotAnnotations | undefined) {
  if (!annotations) {
    return undefined
  }
  return {
    CodeVulnerability: annotations.CodeVulnerability?.map(vuln => ({
      details: {
        type: vuln?.details?.type,
      },
    })),
    PublicCodeReference: annotations.PublicCodeReference?.map(ref => ({
      details: {
        license: ref?.details?.license,
        language: ref?.details?.language,
      },
    })),
  }
}

/**
 * Converts the timings into relative times for reporting.
 */
function stringifyTiming(timings: Timings) {
  const result: Record<string, number> = {
    totalTime: timings.endTime - timings.startTime,
  }
  if (timings.firstByte !== undefined) result['ttfb'] = timings.firstByte - timings.startTime
  if (timings.firstToken !== undefined) result['ttft'] = timings.firstToken - timings.startTime
  return result
}
